'use client';

import React, { useMemo, useState } from 'react';
import {
  ScrollText,
  Wallet,
  Clock,
  Mail,
  MessageSquare,
  CheckCircle2,
  ChevronRight,
  AlertCircle,
  ExternalLink,
  FileText,
  Download,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/api/trpc';
import { formatMoney } from '@/lib/money';
import { usePartnerAdmin } from '@/hooks/use-partner-admin';
import { useAuditPackDownload } from '@/hooks/use-audit-pack-download';
import { useNavigate, useParams } from 'react-router-dom';
import { PARTNER_ADMIN_ROUTES } from '@/config/router';
import type { AppRouter } from '@tagadapay/trpc-types';
import type { inferRouterOutputs } from '@trpc/server';

type StatementPreview = inferRouterOutputs<AppRouter>['partnerReports']['preview'];
type PreviewAccountLine = StatementPreview['byAccount'][number];

function fmtDate(d: Date | string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const STATUS_STYLE: Record<string, string> = {
  finalized: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  sent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  invoiced: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
  paid: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  draft: 'bg-muted text-muted-foreground',
  voided: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
        STATUS_STYLE[status] ?? 'bg-muted text-muted-foreground'
      }`}
    >
      {status}
    </span>
  );
}

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  isLoading,
  className,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  isLoading?: boolean;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="text-muted-foreground h-4 w-4" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-7 w-32" />
        ) : (
          <>
            <div className="text-2xl font-bold tabular-nums">{value}</div>
            {subtitle && <p className="text-muted-foreground text-xs">{subtitle}</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function fmtFreeze(d: Date | string): string {
  return new Date(d).toLocaleString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const DEFAULT_OUTSTANDING_SHOWN = 8;

const byUnpaidDesc = (a: PreviewAccountLine, b: PreviewAccountLine) =>
  b.unpaidCents - a.unpaidCents;

function InvoiceLinks({ line }: { line: PreviewAccountLine }) {
  if (!line.invoiceUrl && !line.invoicePdf) return null;
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      {line.invoicePdf && (
        <a
          href={line.invoicePdf}
          target="_blank"
          rel="noreferrer"
          title={`Invoice PDF${line.invoiceNumber ? ` — ${line.invoiceNumber}` : ''}`}
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-[11px]"
        >
          <FileText className="h-3.5 w-3.5" />
          {line.invoiceNumber ?? 'Invoice'}
        </a>
      )}
      {line.invoiceUrl && (
        <a
          href={line.invoiceUrl}
          target="_blank"
          rel="noreferrer"
          title="Stripe payment page for this invoice"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Pay link
        </a>
      )}
    </span>
  );
}

function OutstandingRow({ line, maxUnpaid }: { line: PreviewAccountLine; maxUnpaid: number }) {
  const barPct = maxUnpaid > 0 ? Math.max(4, Math.round((line.unpaidCents / maxUnpaid) * 100)) : 0;
  const partiallyPaid = line.paidCents > 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="flex min-w-0 items-baseline gap-2">
            <span className="truncate text-sm font-medium">
              {line.accountName || line.accountId}
            </span>
            <InvoiceLinks line={line} />
          </span>
          <span className="whitespace-nowrap text-sm font-semibold tabular-nums text-amber-600">
            {formatMoney(line.unpaidCents, 'USD')}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <div className="bg-muted h-1.5 flex-1 overflow-hidden rounded-full">
            <div className="h-full rounded-full bg-amber-500" style={{ width: `${barPct}%` }} />
          </div>
          <span className="text-muted-foreground whitespace-nowrap text-[11px] tabular-nums">
            +{formatMoney(line.markupUnpaidCents, 'USD')} markup
            {partiallyPaid && ' · partial'}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The collection hit-list: which merchants still owe their invoice this
 * week, biggest amount first, so the partner knows exactly who to chase.
 */
function OutstandingMerchants({ byAccount }: { byAccount: PreviewAccountLine[] }) {
  const [showAll, setShowAll] = useState(false);

  const { unpaid, totalUnpaidCents, paidCount, billedCount } = useMemo(() => {
    const billed = byAccount.filter((a) => a.invoicedCents > 0);
    const rows = billed.filter((a) => a.unpaidCents > 0).sort(byUnpaidDesc);
    return {
      unpaid: rows,
      totalUnpaidCents: rows.reduce((s, a) => s + a.unpaidCents, 0),
      paidCount: billed.length - rows.length,
      billedCount: billed.length,
    };
  }, [byAccount]);

  if (billedCount === 0) return null;

  if (unpaid.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        All {billedCount} invoiced merchants have paid — collection is complete.
      </div>
    );
  }

  const visible = showAll ? unpaid : unpaid.slice(0, DEFAULT_OUTSTANDING_SHOWN);
  const maxUnpaid = unpaid[0]?.unpaidCents ?? 0;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-900 dark:bg-amber-950/30">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-700 dark:text-amber-400">
          <AlertCircle className="h-4 w-4" />
          Still to collect — {unpaid.length} merchant{unpaid.length > 1 ? 's' : ''}
        </p>
        <p className="text-sm font-bold tabular-nums text-amber-700 dark:text-amber-400">
          {formatMoney(totalUnpaidCents, 'USD')}
        </p>
      </div>
      <p className="text-muted-foreground mb-2 text-xs">
        {paidCount} of {billedCount} invoiced merchants fully collected. Largest outstanding first.
      </p>
      <div className="divide-y divide-amber-100 dark:divide-amber-900/50">
        {visible.map((line) => (
          <OutstandingRow
            key={line.billingReportId ?? line.accountId}
            line={line}
            maxUnpaid={maxUnpaid}
          />
        ))}
      </div>
      {unpaid.length > DEFAULT_OUTSTANDING_SHOWN && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-1 h-7 w-full text-xs text-amber-700 hover:text-amber-800 dark:text-amber-400"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? 'Show less' : `Show all ${unpaid.length} outstanding merchants`}
        </Button>
      )}
    </div>
  );
}

/**
 * Live preview of the statement that freezes next Monday: the same compute
 * as the weekly cron, run on the cohort currently in its collection week.
 * Collected/markup figures move day by day until they freeze.
 */
function UpcomingStatementCard({ partner }: { partner: string }) {
  const { data, isLoading, error } = trpc.partnerReports.preview.useQuery(
    { partner },
    { staleTime: 10 * 60 * 1000, refetchOnWindowFocus: false },
  );

  if (error) return null; // preview is best-effort — history below still renders

  const collectedPct =
    data && data.cohortInvoicedCents > 0
      ? Math.round((data.cohortPaidCents / data.cohortInvoicedCents) * 100)
      : 0;
  const reconCents = data ? data.latePaymentMarkupCents - data.refundClawbackCents : 0;

  return (
    <Card className="border-indigo-200 dark:border-indigo-900">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-4 w-4 text-indigo-500" />
            Upcoming statement — live preview
          </CardTitle>
          {data && (
            <Badge variant="outline" className="border-indigo-300 text-indigo-600 dark:text-indigo-400">
              Freezes {fmtFreeze(data.freezesAt)}
            </Badge>
          )}
        </div>
        <CardDescription>
          {data
            ? `Merchant invoices issued for ${fmtDate(data.periodStart)} → ${fmtDate(data.periodEnd)}, now in their collection week. These numbers update as invoices get paid, then freeze into your official statement.`
            : 'Computing the current collection week…'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading || !data ? (
          <Skeleton className="h-20 w-full" />
        ) : data.cohortInvoicedCents === 0 ? (
          <p className="text-muted-foreground py-4 text-center text-sm">
            No merchant invoices in this cohort yet.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-muted-foreground text-xs">Invoiced to merchants</p>
                <p className="text-lg font-semibold tabular-nums">
                  {formatMoney(data.cohortInvoicedCents, 'USD')}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Collected so far</p>
                <p className="text-lg font-semibold tabular-nums text-emerald-600">
                  {formatMoney(data.cohortPaidCents, 'USD')}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Your markup so far</p>
                <p className="text-lg font-semibold tabular-nums text-emerald-600">
                  {formatMoney(data.cohortMarkupPaidCents, 'USD')}
                </p>
                {data.cohortMarkupUnpaidCents > 0 && (
                  <p className="text-muted-foreground text-xs">
                    +{formatMoney(data.cohortMarkupUnpaidCents, 'USD')} pending collection
                  </p>
                )}
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Estimated total owed</p>
                <p className="text-lg font-bold tabular-nums">
                  {formatMoney(data.totalOwedCents, 'USD')}
                </p>
                {reconCents !== 0 && (
                  <p className={`text-xs ${reconCents < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    incl. {reconCents < 0 ? '−' : '+'}
                    {formatMoney(Math.abs(reconCents), 'USD')} reconciliation
                  </p>
                )}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1 flex justify-between text-xs">
                <span>Collection progress</span>
                <span>{collectedPct}%</span>
              </div>
              <div className="bg-muted h-2 overflow-hidden rounded-full">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all"
                  style={{ width: `${collectedPct}%` }}
                />
              </div>
            </div>
            <OutstandingMerchants byAccount={data.byAccount} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PartnerAdminReportsPage() {
  const { partner: partnerParam } = useParams<{ partner: string }>();
  const { partner, canView } = usePartnerAdmin(partnerParam);
  const navigate = useNavigate();
  const { downloadOne, downloadAll, pendingId, downloadingAll, allProgress } =
    useAuditPackDownload(partner ?? '');

  const { data, isLoading, error, refetch } = trpc.partnerReports.list.useQuery(
    { partner: partner ?? undefined },
    { staleTime: 2 * 60 * 1000, refetchOnWindowFocus: false, enabled: canView && !!partner },
  );

  const rows = data ?? [];
  const latest = rows[0];
  const trailing = useMemo(
    () => rows.reduce((s, r) => s + r.totalOwedCents, 0),
    [rows],
  );
  const pending = useMemo(
    () => rows.reduce((s, r) => s + r.cohortMarkupUnpaidCents, 0),
    [rows],
  );

  if (!canView) return null;

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="py-8 text-center">
          <p className="text-destructive font-medium">Failed to load statements</p>
          <p className="text-muted-foreground mt-1 text-sm">{error.message}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Weekly statements</h2>
        <p className="text-muted-foreground text-sm">
          What TagadaPay owes you each week, on a cash basis — your markup on the merchant invoices
          actually collected. Issue your invoice for the amount owed; late payments and
          refunds/chargebacks are reconciled automatically on later statements.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard
          title="Latest statement"
          value={latest ? formatMoney(latest.totalOwedCents, 'USD') : '—'}
          subtitle={
            latest
              ? `${fmtDate(latest.periodStart)} → ${fmtDate(latest.periodEnd)}`
              : 'No statement yet'
          }
          icon={Wallet}
          isLoading={isLoading}
          className="border-emerald-200 dark:border-emerald-900"
        />
        <MetricCard
          title={`Total owed (last ${rows.length || 0} statements)`}
          value={formatMoney(trailing, 'USD')}
          subtitle="Sum of owed amounts on record"
          icon={ScrollText}
          isLoading={isLoading}
        />
        <MetricCard
          title="Pending (not yet collected)"
          value={formatMoney(pending, 'USD')}
          subtitle="Markup on unpaid merchant invoices"
          icon={Clock}
          isLoading={isLoading}
          className="border-amber-200 dark:border-amber-900"
        />
      </div>

      {partner && <UpcomingStatementCard partner={partner} />}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Statement history</CardTitle>
            <CardDescription>One immutable snapshot per billing week</CardDescription>
          </div>
          {rows.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              disabled={downloadingAll || pendingId !== null}
              onClick={() => downloadAll(rows.map((r) => r.id))}
            >
              {downloadingAll ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Downloading {allProgress?.done ?? 0}/{allProgress?.total ?? rows.length}…
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Download all audit packs
                </>
              )}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No statements generated yet. The first one is produced automatically the week after
              your merchants are invoiced.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead className="text-right">Collected</TableHead>
                    <TableHead className="text-right">Reconciliation</TableHead>
                    <TableHead className="text-right">Owed</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Delivered</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const recon = r.latePaymentMarkupCents - r.refundClawbackCents;
                    return (
                      <TableRow
                        key={r.id}
                        className="cursor-pointer"
                        onClick={() =>
                          partner && navigate(PARTNER_ADMIN_ROUTES(partner).REPORT_DETAILS(r.id))
                        }
                      >
                        <TableCell className="text-sm font-medium">
                          {fmtDate(r.periodStart)}
                          <span className="text-muted-foreground"> → {fmtDate(r.periodEnd)}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {r.model === 'buy_rate' ? 'Buy rate' : 'Rev share'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-emerald-600">
                          {formatMoney(r.cohortPaidCents, 'USD')}
                        </TableCell>
                        <TableCell
                          className={`text-right tabular-nums ${
                            recon < 0 ? 'text-red-600' : recon > 0 ? 'text-emerald-600' : 'text-muted-foreground'
                          }`}
                        >
                          {recon === 0
                            ? '—'
                            : `${recon < 0 ? '−' : '+'}${formatMoney(Math.abs(recon), 'USD')}`}
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">
                          {formatMoney(r.totalOwedCents, 'USD')}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={r.status} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            {r.emailSentAt ? (
                              <Mail className="h-3.5 w-3.5 text-emerald-600" />
                            ) : (
                              <Mail className="h-3.5 w-3.5 opacity-30" />
                            )}
                            {r.slackSentAt ? (
                              <MessageSquare className="h-3.5 w-3.5 text-emerald-600" />
                            ) : (
                              <MessageSquare className="h-3.5 w-3.5 opacity-30" />
                            )}
                            {r.partnerInvoiceUrl && (
                              <CheckCircle2 className="h-3.5 w-3.5 text-indigo-600" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title="Download audit pack (PDF + CSVs)"
                              disabled={pendingId !== null || downloadingAll}
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadOne(r.id);
                              }}
                            >
                              {pendingId === r.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                            <ChevronRight className="text-muted-foreground h-4 w-4" />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
